import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    collection,
    getDocs,
    doc,
    getDoc,
    updateDoc,
    setDoc,
    query,
    where,
    orderBy,
    deleteField
} from 'firebase/firestore';
import { db } from '../../../../Services/firebase-config';
import { useAuth } from '../../../../Contexts/AuthContext';
import {
    PlusIcon,
    EyeIcon,
    UserRemoveIcon,
    UserAddIcon,
    XIcon,
    TagIcon,
    UserGroupIcon,
    TrashIcon
} from '@heroicons/react/outline';

const CategoriesAndJudges = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();
    const [categories, setCategories] = useState([]);
    const [judges, setJudges] = useState([]);
    const [applications, setApplications] = useState([]);
    const [categoryAssignments, setCategoryAssignments] = useState({});
    const [applicationCounts, setApplicationCounts] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Modal states
    const [showAddCategoryModal, setShowAddCategoryModal] = useState(false);
    const [showJudgeDetailsModal, setShowJudgeDetailsModal] = useState(false);
    const [showAssignJudgeModal, setShowAssignJudgeModal] = useState(false);
    const [selectedJudge, setSelectedJudge] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [newCategoryDescription, setNewCategoryDescription] = useState('');
    const [judgeApplications, setJudgeApplications] = useState([]);
    const [addingCategory, setAddingCategory] = useState(false);
    const [assigningJudge, setAssigningJudge] = useState(false);
    const [deletingCategory, setDeletingCategory] = useState(null); // Store category name being deleted

    // Dashboard stats
    const [dashboardStats, setDashboardStats] = useState({
        totalCategories: 0,
        totalJudges: 0,
        assignedJudges: 0,
        unassignedJudges: 0
    });

    useEffect(() => {
        console.log('CategoriesAndJudges component mounted');
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch categories
            await fetchCategories();

            // Fetch judges
            await fetchJudges();

            // Fetch applications
            await fetchApplications();

            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setError('Failed to load data. Please try again.');
            setLoading(false);
        }
    };

    const fetchCategories = async () => {
        try {
            const categoriesDocRef = doc(db, 'hackathonCategories', 'categories');
            const categoriesDoc = await getDoc(categoriesDocRef);

            if (categoriesDoc.exists()) {
                const categoriesData = categoriesDoc.data();
                setCategories(categoriesData.categories || []);
            } else {
                setCategories([]);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
            setCategories([]);
        }
    };

    const fetchJudges = async () => {
        try {
            const judgesQuery = query(
                collection(db, 'users'),
                where('role', '==', 'Judge'),
                orderBy('name', 'asc')
            );

            const judgesSnapshot = await getDocs(judgesQuery);
            const judgesData = judgesSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setJudges(judgesData);
            console.log('Judges fetched successfully:', judgesData);

            // Calculate assignments and stats - support multiple categories per judge
            const assignments = {};
            let judgesWithAssignments = 0;

            judgesData.forEach(judge => {
                let hasAssignment = false;

                // Handle multiple category assignments
                if (judge.categoryAssignments && Array.isArray(judge.categoryAssignments)) {
                    judge.categoryAssignments.forEach(assignment => {
                        const categoryName = assignment.category;
                        assignments[categoryName] = assignments[categoryName] || [];
                        assignments[categoryName].push(judge);
                        hasAssignment = true;
                    });
                }
                // Handle legacy single assignment format for backward compatibility
                else if (judge.categoryAssignment) {
                    const categoryName = judge.categoryAssignment.category;
                    assignments[categoryName] = assignments[categoryName] || [];
                    assignments[categoryName].push(judge);
                    hasAssignment = true;
                }

                if (hasAssignment) {
                    judgesWithAssignments++;
                }
            });

            setCategoryAssignments(assignments);
            setDashboardStats(prev => ({
                ...prev,
                totalJudges: judgesData.length,
                assignedJudges: judgesWithAssignments,
                unassignedJudges: judgesData.length - judgesWithAssignments
            }));

        } catch (error) {
            console.error('Error fetching judges:', error);
            setJudges([]);
        }
    };

    const fetchApplications = async () => {
        try {
            const applicationsQuery = query(
                collection(db, 'hackathonApplications'),
                orderBy('submissionDate', 'desc')
            );

            const applicationsSnapshot = await getDocs(applicationsQuery);
            const applicationsData = applicationsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setApplications(applicationsData);

            // Calculate application counts per category
            const counts = {};
            applicationsData.forEach(app => {
                if (app.category) {
                    counts[app.category] = (counts[app.category] || 0) + 1;
                }
            });

            setApplicationCounts(counts);
            console.log('Applications fetched successfully:', applicationsData.length, 'applications');
            console.log('Application counts by category:', counts);

        } catch (error) {
            console.error('Error fetching applications:', error);
            setApplications([]);
            setApplicationCounts({});
        }
    };

    const addCategory = async () => {
        if (!newCategoryName.trim()) return;

        try {
            setAddingCategory(true);
            const categoriesDocRef = doc(db, 'hackathonCategories', 'categories');
            const categoriesDoc = await getDoc(categoriesDocRef);

            let existingCategories = [];
            if (categoriesDoc.exists()) {
                existingCategories = categoriesDoc.data().categories || [];
            }

            // Check if category name already exists
            const categoryExists = existingCategories.some(cat =>
                cat.categoryName === newCategoryName.trim()
            );

            if (categoryExists) {
                alert('Category already exists!');
                setAddingCategory(false);
                return;
            }

            const newCategory = {
                categoryName: newCategoryName.trim(),
                categoryDescription: newCategoryDescription.trim() || ''
            };

            const updatedCategories = [...existingCategories, newCategory];

            await setDoc(categoriesDocRef, {
                categories: updatedCategories
            }, { merge: true });

            setCategories(updatedCategories);
            setDashboardStats(prev => ({ ...prev, totalCategories: updatedCategories.length }));
            setNewCategoryName('');
            setNewCategoryDescription('');
            setShowAddCategoryModal(false);

        } catch (error) {
            console.error('Error adding category:', error);
            alert('Failed to add category. Please try again.');
        } finally {
            setAddingCategory(false);
        }
    };

    const assignJudgeToCategory = async (judgeId, categoryName) => {
        // Check if user has Administrator role
        if (!userData || userData.role !== 'Administrator') {
            alert('Only Administrators can assign judges to categories.');
            return;
        }

        try {
            setAssigningJudge(true);
            const judgeRef = doc(db, 'users', judgeId);

            // Get current judge data
            const judgeDoc = await getDoc(judgeRef);
            const currentJudgeData = judgeDoc.data();

            // Handle multiple category assignments
            let categoryAssignments = [];

            // Check if judge already has assignments
            if (currentJudgeData.categoryAssignments && Array.isArray(currentJudgeData.categoryAssignments)) {
                categoryAssignments = [...currentJudgeData.categoryAssignments];
            } else if (currentJudgeData.categoryAssignment) {
                // Migrate from legacy single assignment to array format
                categoryAssignments = [currentJudgeData.categoryAssignment];
            }

            // Check if judge is already assigned to this category
            const existingAssignment = categoryAssignments.find(assignment => assignment.category === categoryName);
            if (existingAssignment) {
                alert('Judge is already assigned to this category.');
                setAssigningJudge(false);
                return;
            }

            // Add new assignment
            const newAssignment = {
                category: categoryName,
                assignedBy: userData.fullName || userData.email || 'Administrator',
                assignedAt: new Date()
            };

            categoryAssignments.push(newAssignment);

            // Update judge document with new assignments array
            await updateDoc(judgeRef, {
                categoryAssignments: categoryAssignments,
                // Remove legacy single assignment field if it exists
                categoryAssignment: deleteField()
            });

            // Refresh data
            await fetchJudges();
            setShowAssignJudgeModal(false);

        } catch (error) {
            console.error('Error assigning judge:', error);
            alert('Failed to assign judge. Please try again.');
        } finally {
            setAssigningJudge(false);
        }
    };

    const removeJudgeFromCategory = async (judgeId, categoryName = null) => {
        // Check if user has Administrator role
        if (!userData || userData.role !== 'Administrator') {
            alert('Only Administrators can remove judge assignments.');
            return;
        }

        try {
            const judgeRef = doc(db, 'users', judgeId);

            if (categoryName) {
                // Remove specific category assignment
                const judgeDoc = await getDoc(judgeRef);
                const currentJudgeData = judgeDoc.data();

                let categoryAssignments = [];
                if (currentJudgeData.categoryAssignments && Array.isArray(currentJudgeData.categoryAssignments)) {
                    categoryAssignments = currentJudgeData.categoryAssignments.filter(
                        assignment => assignment.category !== categoryName
                    );
                }

                await updateDoc(judgeRef, {
                    categoryAssignments: categoryAssignments
                });
            } else {
                // Remove all assignments (legacy functionality)
                await updateDoc(judgeRef, {
                    categoryAssignment: deleteField(),
                    categoryAssignments: deleteField()
                });
            }

            // Refresh data
            await fetchJudges();

        } catch (error) {
            console.error('Error removing judge assignment:', error);
            alert('Failed to remove assignment. Please try again.');
        }
    };

    const deleteCategory = async (categoryToDelete) => {
        const categoryName = categoryToDelete.categoryName || categoryToDelete; // Handle both old and new formats

        // eslint-disable-next-line no-restricted-globals
        if (!confirm(`Are you sure you want to delete the "${categoryName}" category? This action cannot be undone.`)) {
            return;
        }

        try {
            setDeletingCategory(categoryName);

            // Remove category from the categories document
            const categoriesDocRef = doc(db, 'hackathonCategories', 'categories');
            const categoriesDoc = await getDoc(categoriesDocRef);

            if (categoriesDoc.exists()) {
                const existingCategories = categoriesDoc.data().categories || [];
                const updatedCategories = existingCategories.filter(cat => {
                    const catName = cat.categoryName || cat;
                    return catName !== categoryName;
                });

                await setDoc(categoriesDocRef, {
                    categories: updatedCategories
                }, { merge: true });

                // Update local state
                setCategories(updatedCategories);
                setDashboardStats(prev => ({ ...prev, totalCategories: updatedCategories.length }));

                // Remove category assignments from all judges
                const assignedJudges = categoryAssignments[categoryName] || [];
                for (const judge of assignedJudges) {
                    try {
                        const judgeRef = doc(db, 'users', judge.id);
                        await updateDoc(judgeRef, {
                            categoryAssignment: deleteField()
                        });
                    } catch (judgeError) {
                        console.error(`Error removing category assignment from judge ${judge.id}:`, judgeError);
                    }
                }

                // Refresh judges data
                await fetchJudges();
            }

        } catch (error) {
            console.error('Error deleting category:', error);
            alert('Failed to delete category. Please try again.');
        } finally {
            setDeletingCategory(null);
        }
    };

    // Helper function to check if judge has any category assignments
    const judgeHasAssignments = (judge) => {
        return (judge.categoryAssignments && judge.categoryAssignments.length > 0) ||
            judge.categoryAssignment;
    };

    // Helper function to check if judge is already assigned to a specific category
    const judgeAssignedToCategory = (judge, categoryName) => {
        if (judge.categoryAssignments && Array.isArray(judge.categoryAssignments)) {
            return judge.categoryAssignments.some(assignment => assignment.category === categoryName);
        }
        if (judge.categoryAssignment) {
            return judge.categoryAssignment.category === categoryName;
        }
        return false;
    };

    // Helper function to get judge's assigned categories
    const getJudgeAssignedCategories = (judge) => {
        const categories = [];
        if (judge.categoryAssignments && Array.isArray(judge.categoryAssignments)) {
            categories.push(...judge.categoryAssignments.map(assignment => assignment.category));
        } else if (judge.categoryAssignment) {
            categories.push(judge.categoryAssignment.category);
        }
        return categories;
    };

    const fetchJudgeApplications = async (judgeId, category) => {
        try {
            setJudgeApplications([]);

            // Fetch applications for this category
            const applicationsQuery = query(
                collection(db, 'hackathonApplications'),
                where('category', '==', category),
                orderBy('submissionDate', 'desc')
            );

            const applicationsSnapshot = await getDocs(applicationsQuery);
            const applications = applicationsSnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            setJudgeApplications(applications);

        } catch (error) {
            console.error('Error fetching judge applications:', error);
            setJudgeApplications([]);
        }
    };

    const handleViewJudgeDetails = async (judge) => {
        setSelectedJudge(judge);
        if (judge.categoryAssignment) {
            await fetchJudgeApplications(judge.id, judge.categoryAssignment.category);
        }
        setShowJudgeDetailsModal(true);
    };

    const handleAssignJudge = (category) => {
        // Check if user has Administrator role
        if (!userData || userData.role !== 'Administrator') {
            alert('Only Administrators can assign judges to categories.');
            return;
        }
        setSelectedCategory(category);
        setShowAssignJudgeModal(true);
    };

    const handleCloseAddCategoryModal = () => {
        setNewCategoryName('');
        setNewCategoryDescription('');
        setAddingCategory(false);
        setShowAddCategoryModal(false);
    };

    // Set total categories in stats
    useEffect(() => {
        setDashboardStats(prev => ({ ...prev, totalCategories: categories.length }));
    }, [categories]);

    if (loading) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="flex items-center justify-center h-64">
                        <div className="text-center">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue-1 mx-auto"></div>
                            <p className="mt-4 text-gray-600">Loading categories and judges...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6 bg-gray-50 min-h-screen">
                <div className="max-w-7xl mx-auto">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-center">
                            <XIcon className="h-5 w-5 text-red-500 mr-2" />
                            <p className="text-red-800 font-medium">Error loading data</p>
                        </div>
                        <p className="text-red-700 mt-2">{error}</p>
                        <div className="mt-4">
                            <button
                                onClick={() => {
                                    setError(null);
                                    fetchData();
                                }}
                                className="bg-red-100 px-2 py-1 text-sm font-medium text-red-800 rounded-md hover:bg-red-200"
                            >
                                Try again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Categories & Judges Management</h1>
                    <p className="text-gray-600 mt-1">Manage hackathon categories and judge assignments.</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    {/* Total Categories */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Categories</p>
                                <p className="text-3xl font-bold text-custom-blue-1 mt-2">{dashboardStats.totalCategories}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-blue-50">
                                <TagIcon className="h-8 w-8 text-custom-blue-1" />
                            </div>
                        </div>
                    </div>

                    {/* Total Judges */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Total Judges</p>
                                <p className="text-3xl font-bold text-custom-green-1 mt-2">{dashboardStats.totalJudges}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-green-50">
                                <UserGroupIcon className="h-8 w-8 text-custom-green-1" />
                            </div>
                        </div>
                    </div>

                    {/* Assigned Judges */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Assigned Judges</p>
                                <p className="text-3xl font-bold text-purple-600 mt-2">{dashboardStats.assignedJudges}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-purple-50">
                                <UserAddIcon className="h-8 w-8 text-purple-600" />
                            </div>
                        </div>
                    </div>

                    {/* Unassigned Judges */}
                    <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm font-medium text-gray-600">Unassigned Judges</p>
                                <p className="text-3xl font-bold text-orange-500 mt-2">{dashboardStats.unassignedJudges}</p>
                            </div>
                            <div className="p-3 rounded-lg bg-orange-50">
                                <UserRemoveIcon className="h-8 w-8 text-orange-500" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Add Category Button */}
                {/* Add Category Button - Admin Only */}
                <div className="mb-6">
                    {userData && userData.role === 'Administrator' ? (
                        <button
                            onClick={() => setShowAddCategoryModal(true)}
                            className="inline-flex items-center px-4 py-2 bg-custom-blue-1 text-white rounded-lg hover:bg-custom-blue-1/90 transition-colors"
                        >
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Add New Category
                        </button>
                    ) : (
                        <div className="inline-flex items-center px-4 py-2 bg-gray-100 text-gray-500 rounded-lg">
                            <PlusIcon className="h-5 w-5 mr-2" />
                            Add New Category (Admin Only)
                        </div>
                    )}
                </div>

                {/* Categories and Judges Table */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-900">Categories & Judge Assignments</h3>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Category
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Description
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Judges Count
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Total Applications
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {categories.length > 0 ? (
                                    categories.map((category) => {
                                        const categoryName = category.categoryName || category; // Handle both old and new formats
                                        const assignedJudges = categoryAssignments[categoryName] || [];
                                        const categoryApplications = applications.filter(app => app.category === categoryName);
                                        return (
                                            <tr
                                                key={categoryName}
                                                className="hover:bg-gray-50 cursor-pointer transition-colors"
                                                onClick={() => navigate('/eventdashboard/category-details', {
                                                    state: { category, judges, applications: categoryApplications }
                                                })}
                                            >
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    {categoryName}
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-700">
                                                    <div className="max-w-xs truncate">
                                                        {category.categoryDescription || 'No description'}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div className="flex items-center gap-2">
                                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                            {assignedJudges.length} {assignedJudges.length === 1 ? 'Judge' : 'Judges'}
                                                        </span>
                                                        {assignedJudges.length === 0 && (
                                                            <span className="text-gray-500 text-sm">None assigned</span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        {applicationCounts[categoryName] || 0} Applications
                                                    </span>
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex items-center gap-2">
                                                        {userData && userData.role === 'Administrator' && (
                                                            <>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAssignJudge(categoryName);
                                                                    }}
                                                                    disabled={assigningJudge}
                                                                    className="inline-flex items-center gap-1 text-custom-blue-1 hover:text-custom-blue-1/80 font-medium disabled:opacity-50"
                                                                >
                                                                    <UserAddIcon className="h-4 w-4" />
                                                                    {assigningJudge ? 'Assigning...' : 'Assign Judge'}
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        deleteCategory(category);
                                                                    }}
                                                                    disabled={deletingCategory === categoryName}
                                                                    className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 font-medium disabled:opacity-50"
                                                                >
                                                                    <TrashIcon className="h-4 w-4" />
                                                                    {deletingCategory === categoryName ? 'Deleting...' : ''}
                                                                </button>
                                                            </>
                                                        )}
                                                        {(!userData || userData.role !== 'Administrator') && (
                                                            <span className="text-sm text-gray-400">View Only Access</span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                                            No categories found. Add a category to get started.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* All Judges Section */}
                {judges.length > 0 && (
                    <div className="mt-8 bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <h3 className="text-lg font-semibold text-gray-900">All Judges & Their Assignments</h3>
                        </div>
                        <div className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                {judges.map((judge) => {
                                    const assignedCategories = getJudgeAssignedCategories(judge);
                                    return (
                                        <div key={judge.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                                            <div className="flex items-center justify-between">
                                                <div className="flex-1">
                                                    <h4 className="font-medium text-gray-900">{judge.name}</h4>
                                                    <p className="text-sm text-gray-500">{judge.email}</p>
                                                    {assignedCategories.length > 0 ? (
                                                        <div className="mt-2">
                                                            <p className="text-xs text-gray-600 mb-1">Assigned to:</p>
                                                            <div className="flex flex-wrap gap-1">
                                                                {assignedCategories.map((category, index) => (
                                                                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                                        {category}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <p className="text-xs text-gray-400 mt-2">No category assignments</p>
                                                    )}
                                                </div>
                                                <button
                                                    onClick={() => handleViewJudgeDetails(judge)}
                                                    className="text-custom-blue-1 hover:text-custom-blue-1/80 ml-2"
                                                >
                                                    <EyeIcon className="h-5 w-5" />
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })
                                }
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Add Category Modal */}
            {showAddCategoryModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Add New Category</h3>
                            <button
                                onClick={handleCloseAddCategoryModal}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XIcon className="h-6 w-6" />
                            </button>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category Name
                            </label>
                            <input
                                type="text"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-blue-1"
                                placeholder="Enter category name"
                                required
                            />
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Category Description
                            </label>
                            <textarea
                                value={newCategoryDescription}
                                onChange={(e) => setNewCategoryDescription(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-blue-1"
                                placeholder="Enter category description (optional)"
                                rows="3"
                            />
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={handleCloseAddCategoryModal}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={addCategory}
                                disabled={addingCategory || !newCategoryName.trim()}
                                className="flex-1 px-4 py-2 bg-custom-blue-1 text-white rounded-md hover:bg-custom-blue-1/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {addingCategory ? (
                                    <>
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                        Adding...
                                    </>
                                ) : (
                                    'Add Category'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Assign Judge Modal */}
            {showAssignJudgeModal && selectedCategory && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-md">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Assign Judge to {selectedCategory}</h3>
                            <button
                                onClick={() => setShowAssignJudgeModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XIcon className="h-6 w-6" />
                            </button>
                        </div>
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Select Judge
                            </label>
                            <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md">
                                {judges
                                    .filter(judge => !judgeAssignedToCategory(judge, selectedCategory))
                                    .map((judge) => {
                                        const assignedCategories = getJudgeAssignedCategories(judge);
                                        return (
                                            <div
                                                key={judge.id}
                                                onClick={() => !assigningJudge && assignJudgeToCategory(judge.id, selectedCategory)}
                                                className={`p-3 border-b border-gray-100 last:border-b-0 ${assigningJudge
                                                    ? 'cursor-not-allowed opacity-50'
                                                    : 'hover:bg-gray-50 cursor-pointer'
                                                    }`}
                                            >
                                                <div className="font-medium text-gray-900">{judge.name}</div>
                                                <div className="text-sm text-gray-500">{judge.email}</div>
                                                {assignedCategories.length > 0 && (
                                                    <div className="text-xs text-blue-600 mt-1">
                                                        Currently assigned to: {assignedCategories.join(', ')}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                }
                                {judges.filter(judge => !judgeAssignedToCategory(judge, selectedCategory)).length === 0 && (
                                    <div className="p-3 text-center text-gray-500">
                                        No available judges for this category
                                    </div>
                                )}
                            </div>
                        </div>
                        {assigningJudge && (
                            <div className="mb-4 flex items-center justify-center text-custom-blue-1">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-custom-blue-1 mr-2"></div>
                                Assigning judge...
                            </div>
                        )}
                        <button
                            onClick={() => setShowAssignJudgeModal(false)}
                            disabled={assigningJudge}
                            className="w-full px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Judge Details Modal */}
            {showJudgeDetailsModal && selectedJudge && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-gray-900">Judge Details: {selectedJudge.name}</h3>
                            <button
                                onClick={() => setShowJudgeDetailsModal(false)}
                                className="text-gray-400 hover:text-gray-600"
                            >
                                <XIcon className="h-6 w-6" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                            <div>
                                <h4 className="font-medium text-gray-900 mb-2">Judge Information</h4>
                                <div className="space-y-2 text-sm">
                                    <p><span className="font-medium">Name:</span> {selectedJudge.name}</p>
                                    <p><span className="font-medium">Email:</span> {selectedJudge.email}</p>
                                    <p><span className="font-medium">Role:</span> {selectedJudge.role}</p>
                                </div>
                            </div>
                            <div>
                                <h4 className="font-medium text-gray-900 mb-2">Assignment Information</h4>
                                <div className="space-y-2 text-sm">
                                    {(() => {
                                        const assignedCategories = getJudgeAssignedCategories(selectedJudge);
                                        if (assignedCategories.length > 0) {
                                            return (
                                                <div>
                                                    <p><span className="font-medium">Assigned Categories ({assignedCategories.length}):</span></p>
                                                    <div className="mt-2 space-y-2">
                                                        {selectedJudge.categoryAssignments && Array.isArray(selectedJudge.categoryAssignments) ?
                                                            selectedJudge.categoryAssignments.map((assignment, index) => (
                                                                <div key={index} className="bg-blue-50 p-3 rounded-lg">
                                                                    <p className="font-medium text-blue-900">{assignment.category}</p>
                                                                    <p className="text-xs text-blue-700">Assigned by: {assignment.assignedBy}</p>
                                                                    <p className="text-xs text-blue-700">Assigned at: {assignment.assignedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
                                                                </div>
                                                            )) :
                                                            selectedJudge.categoryAssignment && (
                                                                <div className="bg-blue-50 p-3 rounded-lg">
                                                                    <p className="font-medium text-blue-900">{selectedJudge.categoryAssignment.category}</p>
                                                                    <p className="text-xs text-blue-700">Assigned by: {selectedJudge.categoryAssignment.assignedBy}</p>
                                                                    <p className="text-xs text-blue-700">Assigned at: {selectedJudge.categoryAssignment.assignedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}</p>
                                                                </div>
                                                            )
                                                        }
                                                    </div>
                                                </div>
                                            );
                                        } else {
                                            return <p className="text-gray-500">No category assignments</p>;
                                        }
                                    })()}
                                </div>
                            </div>
                        </div>

                        {getJudgeAssignedCategories(selectedJudge).length > 0 && (
                            <div>
                                <h4 className="font-medium text-gray-900 mb-4">Applications from Assigned Categories</h4>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Application ID
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Team Leader
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Submission Date
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                    Status
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {judgeApplications.length > 0 ? (
                                                judgeApplications.map((application) => (
                                                    <tr key={application.id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-custom-blue-1">
                                                            {application.id.substring(0, 8)}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {application.teamLeaderName || application.fullName || 'N/A'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                            {application.submissionDate?.toDate?.()?.toLocaleDateString() || 'N/A'}
                                                        </td>
                                                        <td className="px-6 py-4 whitespace-nowrap">
                                                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${application.isEvaluated ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800'
                                                                }`}>
                                                                {application.isEvaluated ? 'Evaluated' : 'Pending Evaluation'}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                                        No applications found for this category
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="mt-6 flex gap-3">
                            {userData && userData.role === 'Administrator' && getJudgeAssignedCategories(selectedJudge).length > 0 && (
                                <div className="flex flex-wrap gap-2">
                                    {getJudgeAssignedCategories(selectedJudge).map((category, index) => (
                                        <button
                                            key={index}
                                            onClick={() => {
                                                removeJudgeFromCategory(selectedJudge.id, category);
                                                setShowJudgeDetailsModal(false);
                                            }}
                                            className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 text-sm"
                                        >
                                            Remove from {category}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() => {
                                            if (window.confirm('Are you sure you want to remove this judge from ALL categories?')) {
                                                removeJudgeFromCategory(selectedJudge.id);
                                                setShowJudgeDetailsModal(false);
                                            }
                                        }}
                                        className="px-3 py-1 bg-red-800 text-white rounded-md hover:bg-red-900 text-sm"
                                    >
                                        Remove All Assignments
                                    </button>
                                </div>
                            )}
                            <button
                                onClick={() => setShowJudgeDetailsModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoriesAndJudges;
